import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  NgZone,
  signal,
  viewChild,
} from '@angular/core';
import { AiFeaturesService } from '@maximizer/ai/data-access';
import { SessionService } from '@maximizer/core/shared/data-access';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { ActionEventType } from '@maximizer/core/shared/ui/config';
import { catchError, map, of, Subscription, take } from 'rxjs';
import { AiHubClassicComponent } from '../ai-hub-classic/ai-hub-classic.component';
import { AiHubV2Component } from '../ai-hub-v2/ai-hub-v2.component';
import { AiHubVersion } from './models/ai-hub-version.model';

@Component({
  selector: 'ai-hub',
  templateUrl: './ai-hub.component.html',
  styleUrl: './ai-hub.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AiHubClassicComponent, AiHubV2Component],
})
export class AiHubComponent extends EmbeddedComponent {
  @HostBinding('class') hostClasses = 'ai-ng';

  private readonly aiFeatures = inject(AiFeaturesService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);
  private readonly session = inject(SessionService);
  private pendingInitialize?: Subscription;

  private readonly hubView = viewChild<EmbeddedComponent>('hubView');

  protected readonly version = signal<AiHubVersion | null>(null);

  constructor() {
    super(inject(ElementRef));
  }

  override doAction(name: string, args?: unknown): void {
    if (name === ActionEventType.Initialize) {
      this.ngZone.run(() => {
        this.pendingInitialize?.unsubscribe();
        this.pendingInitialize = this.session
          .getInfo()
          .pipe(
            take(1),
            map(() => undefined),
            catchError(() => of(undefined)),
          )
          .subscribe(() => this.showHub(name, args));
        this.addSubscription(this.pendingInitialize);
      });
    } else {
      super.doAction(name, args);
    }
  }

  private showHub(name: string, args?: unknown): void {
    this.version.set(this.aiFeatures.isIQBoostV2Enabled() ? 'v2' : 'classic');
    this.changeDetector.detectChanges();
    this.hubView()?.doAction(name, args);
  }
}
